import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Chip, Button, CircularProgress, Alert } from '@mui/material';
import { CheckCircle, Schedule, Cancel, AccessTime, Refresh } from '@mui/icons-material';
import driverService, { Guarantor } from '../../services/driverService';
import { toast } from 'react-hot-toast';

interface GuarantorSectionProps {
  driverId: string;
}

export default function GuarantorSection({ driverId }: GuarantorSectionProps) {
  const [guarantors, setGuarantors] = useState<Guarantor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resendingFor, setResendingFor] = useState<number | null>(null);

  useEffect(() => {
    loadGuarantors();
  }, [driverId]);

  const loadGuarantors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await driverService.getGuarantors(driverId);
      setGuarantors(response.guarantors || []);
    } catch (err: any) {
      console.error('Failed to load guarantors:', err);
      setError(err.message || 'Failed to load guarantor information');
    } finally {
      setLoading(false);
    }
  };

  const handleResendSMS = async (guarantorNumber: 1 | 2) => {
    try {
      setResendingFor(guarantorNumber);
      const result = await driverService.resendGuarantorSMS(driverId, guarantorNumber);
      toast.success(result.message || `SMS resent to guarantor ${guarantorNumber}`);
      // Reload guarantors to update SMS sent timestamp
      await loadGuarantors();
    } catch (err: any) {
      console.error('Failed to resend SMS:', err);
      toast.error(err.message || 'Failed to resend SMS');
    } finally {
      setResendingFor(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />;
      case 'pending':
        return <Schedule sx={{ fontSize: 20, color: 'warning.main' }} />;
      case 'declined':
        return <Cancel sx={{ fontSize: 20, color: 'error.main' }} />;
      case 'expired':
        return <AccessTime sx={{ fontSize: 20, color: 'text.disabled' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'pending':
        return 'warning';
      case 'declined':
        return 'error';
      case 'expired':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Loading guarantor information...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={loadGuarantors} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (guarantors.length === 0) {
    return (
      <Alert severity="info">
        No guarantor information available for this driver.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        Guarantors
        <Chip label={guarantors.length} size="small" color="primary" />
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {guarantors.map((guarantor) => (
          <Card key={guarantor.guarantorNumber} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Guarantor {guarantor.guarantorNumber}
                  </Typography>
                  {getStatusIcon(guarantor.status)}
                  <Chip
                    label={getStatusLabel(guarantor.status)}
                    color={getStatusColor(guarantor.status)}
                    size="small"
                  />
                </Box>

                {guarantor.status === 'pending' && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={resendingFor === guarantor.guarantorNumber ? <CircularProgress size={16} /> : <Refresh />}
                    onClick={() => handleResendSMS(guarantor.guarantorNumber)}
                    disabled={resendingFor === guarantor.guarantorNumber}
                  >
                    Resend SMS
                  </Button>
                )}

                {guarantor.status === 'expired' && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={resendingFor === guarantor.guarantorNumber ? <CircularProgress size={16} /> : <Refresh />}
                    onClick={() => handleResendSMS(guarantor.guarantorNumber)}
                    disabled={resendingFor === guarantor.guarantorNumber}
                  >
                    Resend Link
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {guarantor.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {guarantor.phone}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Relationship
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {guarantor.relationship}
                  </Typography>
                </Box>

                {guarantor.verifiedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Verified At
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(guarantor.verifiedAt)}
                    </Typography>
                  </Box>
                )}

                {guarantor.smsSent && guarantor.smsSentAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      SMS Sent At
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(guarantor.smsSentAt)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
